import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon, ClockIcon, CheckCircleIcon,
  XCircleIcon, FunnelIcon, XMarkIcon,
} from '@heroicons/react/24/outline';

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'}) : '—';
const fmtDateTime = d => d ? new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sbr * { box-sizing:border-box; }
  .sbr { font-family:'Sora',sans-serif; color:#1a1d2e; animation:sbrFade .4s ease both; }
  @keyframes sbrFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .sbr-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .sbr-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .sbr-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }

  /* Stat chips */
  .sbr-chips { display:flex; gap:8px; margin-bottom:1.4rem; flex-wrap:wrap; }
  .sbr-chip  { padding:6px 16px; border-radius:99px; font-size:.76rem; font-weight:700; cursor:pointer; border:1.5px solid; transition:all .15s; }
  .sbr-chip.all      { background:white;    color:#6b7194;  border-color:#e0e4f0; }
  .sbr-chip.pending  { background:#fffbeb;  color:#f59e0b;  border-color:#fde68a; }
  .sbr-chip.approved { background:#ecfdf5;  color:#10c98e;  border-color:#a7f3d0; }
  .sbr-chip.rejected { background:#fef2f2;  color:#ef4444;  border-color:#fecaca; }
  .sbr-chip.active   { box-shadow:0 2px 8px rgba(0,0,0,.1); transform:translateY(-1px); }

  /* Request cards */
  .sbr-list { display:flex; flex-direction:column; gap:.9rem; }
  .sbr-card { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; transition:box-shadow .15s; }
  .sbr-card:hover { box-shadow:0 4px 16px rgba(0,0,0,.06); }
  .sbr-card-body { padding:1.2rem 1.4rem; }
  .sbr-card-top  { display:flex; align-items:flex-start; gap:14px; margin-bottom:1rem; }
  .sbr-icon { width:44px; height:44px; border-radius:12px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .sbr-icon svg { width:20px; height:20px; stroke-width:1.8; }
  .sbr-icon.pending  { background:#fffbeb; color:#f59e0b; }
  .sbr-icon.approved { background:#ecfdf5; color:#10c98e; }
  .sbr-icon.rejected { background:#fef2f2; color:#ef4444; }
  .sbr-name    { font-size:.96rem; font-weight:700; color:#0f1123; margin-bottom:4px; }
  .sbr-owner   { font-size:.74rem; color:#7c3aed; font-weight:600; margin-bottom:3px; }
  .sbr-meta    { font-size:.7rem; color:#9094a8; }
  .sbr-badge   { display:inline-flex; align-items:center; gap:4px; font-size:.67rem; font-weight:700; padding:3px 10px; border-radius:99px; margin-left:8px; }
  .sbr-badge.pending  { background:#fffbeb; color:#f59e0b; }
  .sbr-badge.approved { background:#ecfdf5; color:#10c98e; }
  .sbr-badge.rejected { background:#fef2f2; color:#ef4444; }

  /* Detail grid */
  .sbr-detail { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-bottom:1rem; background:#f8f9fc; border-radius:10px; padding:12px 14px; }
  .sbr-drow   { display:flex; flex-direction:column; gap:2px; }
  .sbr-dlabel { font-size:.62rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .sbr-dval   { font-size:.78rem; color:#1a1d2e; font-weight:500; }
  .sbr-dval.mono { font-family:'JetBrains Mono',monospace; }

  /* Review note */
  .sbr-review-note { font-size:.74rem; padding:8px 12px; border-radius:9px; margin-top:6px; }
  .sbr-review-note.approved { background:#ecfdf5; color:#065f46; border-left:3px solid #10c98e; }
  .sbr-review-note.rejected { background:#fef2f2; color:#991b1b; border-left:3px solid #ef4444; }

  /* Action buttons */
  .sbr-actions { display:flex; gap:8px; flex-wrap:wrap; }
  .btn-approve { display:inline-flex; align-items:center; gap:6px; padding:8px 18px; border:none; border-radius:10px; background:#10c98e; color:white; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:700; cursor:pointer; transition:all .18s; }
  .btn-approve:hover { background:#0db87e; box-shadow:0 3px 12px rgba(16,201,142,.35); transform:translateY(-1px); }
  .btn-reject  { display:inline-flex; align-items:center; gap:6px; padding:8px 18px; border:1.5px solid #fecaca; border-radius:10px; background:#fef2f2; color:#ef4444; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:700; cursor:pointer; transition:all .18s; }
  .btn-reject:hover  { background:#ef4444; color:white; border-color:#ef4444; }
  .btn-approve:disabled,.btn-reject:disabled { opacity:.5; cursor:not-allowed; transform:none !important; box-shadow:none !important; }

  /* Modal */
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal { background:white; border-radius:20px; width:100%; max-width:480px; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:moIn .25s ease; overflow:hidden; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head  { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-body  { padding:20px 22px; }
  .modal-footer { display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; }
  .modal-info  { background:#f8f9fc; border-radius:10px; padding:12px 14px; margin-bottom:14px; font-size:.8rem; }
  .modal-info-name { font-weight:700; color:#0f1123; margin-bottom:2px; }
  .modal-info-sub  { font-size:.72rem; color:#9094a8; }
  .form-field  { display:flex; flex-direction:column; gap:5px; margin-bottom:12px; }
  .form-label  { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-textarea:focus { border-color:#3b6ef8; background:white; }
  .form-textarea { resize:vertical; min-height:70px; }

  .sbr-empty { text-align:center; padding:3.5rem 2rem; background:white; border-radius:16px; border:1px solid #e8ebf4; }
  .sbr-empty-icon { width:52px; height:52px; border-radius:14px; background:#fffbeb; color:#f59e0b; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; }
  .sbr-empty-icon svg { width:24px; height:24px; stroke-width:1.6; }
  .sbr-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .sbr-spin { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  @media (max-width:600px) { .sbr-detail { grid-template-columns:1fr; } .sbr-actions { flex-direction:column; } .modal { border-radius:16px 16px 0 0; } .modal-backdrop { align-items:flex-end; padding:0; } }
`;

export default function SuperBranchRequests() {
  const [requests,   setRequests]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState('all');
  const [actionModal,setActionModal]= useState(null); // { req, type: 'approve'|'reject' }
  const [note,       setNote]       = useState('');
  const [reason,     setReason]     = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/super/branch-requests${filter !== 'all' ? `?status=${filter}` : ''}`);
      setRequests(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load requests'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRequests(); }, [filter]);

  const openApprove = (req) => { setActionModal({ req, type:'approve' }); setNote(''); setReason(''); };
  const openReject  = (req) => { setActionModal({ req, type:'reject'  }); setNote(''); setReason(''); };

  const handleSubmit = async () => {
    if (!actionModal) return;
    const { req, type } = actionModal;
    if (type === 'reject' && !reason.trim()) return toast.error('Please provide a reason for rejection');
    setSubmitting(true);
    try {
      if (type === 'approve') {
        await api.put(`/super/branch-requests/${req._id}/approve`, { superNote: note });
        toast.success(`Branch "${req.name}" approved and created ✓`);
      } else {
        await api.put(`/super/branch-requests/${req._id}/reject`, { rejectReason: reason, superNote: note });
        toast.success('Request rejected');
      }
      setActionModal(null);
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const all      = requests.length;
  const pending  = requests.filter(r => r.status==='pending').length;
  const approved = requests.filter(r => r.status==='approved').length;
  const rejected = requests.filter(r => r.status==='rejected').length;

  const CHIPS = [
    { key:'all',      label:'All' },
    { key:'approved', label:`Approved ${approved>0?`(${approved})`:''}` },
    { key:'pending',  label:`Pending ${pending>0?`(${pending})`:''}` },
    { key:'rejected', label:'Rejected' },
  ];

  return (
    <div className="sbr"><style>{css}</style>

      <div className="sbr-hdr">
        <div>
          <div className="sbr-title">Branch Requests</div>
          <div className="sbr-sub">Review and approve new school branch requests from owners</div>
        </div>
      </div>

      {/* Filter chips */}
      <div className="sbr-chips">
        {CHIPS.map(c => (
          <button key={c.key} className={`sbr-chip ${c.key} ${filter===c.key?'active':''}`}
            onClick={() => setFilter(c.key)}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="sbr-loading"><div className="sbr-spin"/>Loading…</div>
      ) : requests.length === 0 ? (
        <div className="sbr-empty">
          <div className="sbr-empty-icon"><ClockIcon /></div>
          <div style={{fontWeight:600,color:'#1a1d2e',marginBottom:4}}>
            {filter==='pending' ? 'No pending requests' : (filter==='all' ? 'No branch requests yet' : `No ${filter} requests`)}
          </div>
          <div style={{fontSize:'.76rem',color:'#9094a8'}}>
            {filter==='pending' ? 'All branch requests have been reviewed.' : 'Nothing to show here.'}
          </div>
        </div>
      ) : (
        <div className="sbr-list">
          {requests.map(r => {
            const Icon = r.status==='approved' ? CheckCircleIcon : r.status==='rejected' ? XCircleIcon : ClockIcon;
            return (
              <div key={r._id} className="sbr-card">
                <div className="sbr-card-body">
                  <div className="sbr-card-top">
                    <div className={`sbr-icon ${r.status}`}><Icon /></div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:'flex',alignItems:'center',flexWrap:'wrap',gap:4}}>
                        <div className="sbr-name">{r.name}</div>
                        <span className={`sbr-badge ${r.status}`}>
                          {r.status==='pending'?'⏳ Pending':r.status==='approved'?'✓ Approved':'✗ Rejected'}
                        </span>
                      </div>
                      <div className="sbr-owner">
                        👤 {r.owner?.name || r.ownerName || '—'} · {r.owner?.email || r.ownerEmail || '—'}
                      </div>
                      <div className="sbr-meta">Submitted {fmtDateTime(r.createdAt)}</div>
                    </div>
                  </div>

                  {/* Detail grid */}
                  <div className="sbr-detail">
                    <div className="sbr-drow">
                      <div className="sbr-dlabel">Branch Name</div>
                      <div className="sbr-dval">{r.name}</div>
                    </div>
                    <div className="sbr-drow">
                      <div className="sbr-dlabel">Address</div>
                      <div className="sbr-dval">{r.address || '—'}</div>
                    </div>
                    <div className="sbr-drow">
                      <div className="sbr-dlabel">Admin Email</div>
                      <div className="sbr-dval mono">{r.adminEmail}</div>
                    </div>
                    <div className="sbr-drow">
                      <div className="sbr-dlabel">Admin Name</div>
                      <div className="sbr-dval">{r.adminName || '—'}</div>
                    </div>
                    {r.phone && <div className="sbr-drow"><div className="sbr-dlabel">Phone</div><div className="sbr-dval mono">{r.phone}</div></div>}
                    {r.email && <div className="sbr-drow"><div className="sbr-dlabel">School Email</div><div className="sbr-dval mono">{r.email}</div></div>}
                  </div>

                  {/* Review info */}
                  {r.status !== 'pending' && (
                    <div className={`sbr-review-note ${r.status}`}>
                      {r.status==='approved'
                        ? `✓ Approved on ${fmtDate(r.reviewedAt)} by ${r.reviewedBy}.`
                        : `✗ Rejected on ${fmtDate(r.reviewedAt)} by ${r.reviewedBy}. Reason: ${r.rejectReason || 'None given'}`}
                      {r.superNote && ` · Note: ${r.superNote}`}
                    </div>
                  )}

                  {/* Actions */}
                  {r.status === 'pending' && (
                    <div className="sbr-actions">
                      <button className="btn-approve" onClick={() => openApprove(r)}>
                        <CheckCircleIcon style={{width:15,height:15,strokeWidth:2.5}}/>Approve Branch
                      </button>
                      <button className="btn-reject" onClick={() => openReject(r)}>
                        <XCircleIcon style={{width:15,height:15,strokeWidth:2.5}}/>Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Action Modal */}
      {actionModal && createPortal(
        <div className="modal-backdrop" onClick={() => setActionModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">
                {actionModal.type==='approve' ? '✓ Approve Branch' : '✗ Reject Request'}
              </div>
              <button className="modal-close" onClick={() => setActionModal(null)}><XMarkIcon /></button>
            </div>
            <div className="modal-body">
              <div className="modal-info">
                <div className="modal-info-name">{actionModal.req.name}</div>
                <div className="modal-info-sub">
                  Owner: {actionModal.req.owner?.name || actionModal.req.ownerName} ·
                  Admin: {actionModal.req.adminEmail}
                </div>
              </div>

              {actionModal.type === 'approve' ? (
                <div style={{background:'#ecfdf5',border:'1px solid #a7f3d0',borderRadius:10,padding:'10px 14px',fontSize:'.78rem',color:'#065f46',marginBottom:14}}>
                  This will <strong>create the school</strong> and <strong>admin account</strong> immediately.
                  The owner will be able to enter this branch from their dashboard.
                </div>
              ) : (
                <div className="form-field">
                  <label className="form-label">Reason for Rejection *</label>
                  <textarea className="form-textarea" placeholder="e.g. Duplicate request, missing info, quota exceeded…"
                    value={reason} onChange={e => setReason(e.target.value)} />
                </div>
              )}

              <div className="form-field">
                <label className="form-label">Note to Owner (optional)</label>
                <textarea className="form-textarea" style={{minHeight:55}}
                  placeholder="Add an optional note visible to the owner…"
                  value={note} onChange={e => setNote(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button style={{padding:'8px 18px',border:'1.5px solid #e0e4f0',borderRadius:10,background:'white',color:'#6b7194',fontFamily:'Sora',fontSize:'.8rem',fontWeight:600,cursor:'pointer'}}
                onClick={() => setActionModal(null)}>Cancel</button>
              {actionModal.type === 'approve'
                ? <button className="btn-approve" onClick={handleSubmit} disabled={submitting}>{submitting?'Creating…':'Confirm Approval'}</button>
                : <button className="btn-reject"  onClick={handleSubmit} disabled={submitting}>{submitting?'Rejecting…':'Confirm Rejection'}</button>
              }
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
}